import { useMemo } from "react";
import { useFirestore } from "hooks/firestore/core/useFirestore";
import { bettingEvent } from "hooks/firestore/models/bettingEvent";
import { filterMarkets, processBettingEvent, processMarket } from "./shared";
import { useCampaigns } from "hooks/firestore/user/useCampaigns";
import { useTokens } from "hooks/firestore/user/useTokens";
import {
  useHasPermission,
  usePermissions,
} from "@/features/auth/hooks/usePermission";
import { racingEvent } from "hooks/firestore/models/racingEvent";
import { where, type QueryConstraint } from "firebase/firestore";
import { type Market, market } from "hooks/firestore/models/market";
import { bettingCompetitor } from "hooks/firestore/models/bettingCompetitor";
import type { Always, NullableMaybe } from "types/utilities";
import {
  type ProcessedRacingEvent,
  useRacingEvent,
} from "@/hooks/firestore/betting/useRacingEvents";
import { useRacingEventCompetitors } from "@/hooks/firestore/betting/useRacingEvents";

export enum AllowedSelectionTypesEnum {
  SameEventMulti = "SameEventMulti",
  SingleOutcome = "SingleOutcome",
  Exotic = "Exotic",
}

export type MarketType = "all" | "live" | "sem";

type EventType = "betting" | "racing";

const getMarketsQuery = (marketFilterType: MarketType) => {
  const query: QueryConstraint[] = [];

  if (marketFilterType === "sem") {
    query.push(
      where(
        "allowedSelectionTypes",
        "array-contains",
        AllowedSelectionTypesEnum.SameEventMulti,
      ),
    );
  } else {
    query.push(
      where("allowedSelectionTypes", "array-contains-any", [
        AllowedSelectionTypesEnum.SingleOutcome,
        AllowedSelectionTypesEnum.Exotic,
      ]),
    );
  }

  return query;
};

// Reads the doc by id, skipping the allowedSelectionTypes filter on the markets
// subcollection. Gets back a market the filtered feed drops (e.g. a settled one
// whose allowedSelectionTypes was cleared).
const useBettingMarket = (
  eventId: NullableMaybe<string>,
  marketId: NullableMaybe<string>,
) => {
  const { data, isLoading, error } = useFirestore(
    eventId && marketId
      ? `/bettingEvents/${eventId}/markets/${marketId}`
      : null,
    market,
    { keyPath: "id" },
  );

  return { market: data, isLoading, error };
};

const isVisibleOnTab = (
  marketFilterType: MarketType,
  market?: ProcessedMarket,
) => {
  if (!market) return false;
  if (marketFilterType === "sem") {
    return market?.allowedSelectionTypes?.includes(
      AllowedSelectionTypesEnum.SameEventMulti,
    );
  }
  return true;
};

const useBettingEvent = (eventId: NullableMaybe<string>) => {
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();
  const { tokens } = useTokens();

  const { data, error, isLoading } = useFirestore(
    eventId && `/bettingEvents/${eventId}`,
    bettingEvent,
    {
      keyPath: "id",
    },
  );

  const processedEvent = useMemo(() => {
    if (!data) return;

    return processBettingEvent(data, codes, tokens, permissions);
  }, [data, codes, tokens, permissions]);

  return {
    isLoading: isLoading || codesLoading,
    error,
    event: processedEvent,
  };
};

const useEventMarkets = (
  eventId: NullableMaybe<string>,
  marketTypes: MarketType,
  eventType: EventType,
) => {
  const {
    data,
    error,
    isLoading: marketsLoading,
  } = useFirestore(
    eventId &&
      `/${eventType === "betting" ? "bettingEvents" : "racingEvents"}/${eventId}/markets`,
    market.array(),
    {
      keyPath: "id",
      query: getMarketsQuery(marketTypes),
    },
  );

  const permissions = usePermissions();
  const { codes, isLoading: codesLoading } = useCampaigns();
  const { tokens, isLoading: tokensLoading } = useTokens();

  const isLoading = marketsLoading || codesLoading || tokensLoading;

  const markets = useMemo(() => {
    if (isLoading || !data) return;

    return data.map((market) => {
      return processMarket(market, codes, tokens, permissions);
    });
  }, [isLoading, data, codes, tokens, permissions]);

  return {
    markets,
    error,
    isLoading,
  };
};

const useBettingEventCompetitors = (eventId: NullableMaybe<string>) => {
  const { data, error, isLoading } = useFirestore(
    eventId && `/bettingEvents/${eventId}/competitorsV2`,
    bettingCompetitor.array(),
    {
      keyPath: "id",
    },
  );

  return {
    competitors: data,
    error,
    isLoading,
  };
};

const decorateOutcomes = (
  outcomes: ProcessedMarket["outcomes"],
  event: ProcessedBettingEvent,
) =>
  Object.entries(outcomes).reduce<DecoratedOutcomes>(
    (decoratedOutcomes, [outcomeId, outcome]) => {
      if (
        outcome.type?.includes("CORRECT_SCORE") &&
        outcome.competitorId &&
        event.competitors
      ) {
        // we need to add competitors to correct score outcomes
        const competitor = event.competitors[outcome.competitorId];

        if (!competitor) return decoratedOutcomes;

        decoratedOutcomes[outcomeId] = {
          ...outcome,
          name: `${competitor.name} - ${outcome.name}`,
        };

        return decoratedOutcomes;
      }

      if (
        ["PLAYER_AWAY", "PLAYER_HOME"].some((type) =>
          outcome.type?.includes(type),
        ) &&
        event.mainMarket
      ) {
        // if our outcome is away or home type, we can override
        // competitor id to get avatar/abbreviations from main market
        // this allows us to render it nicely despite the fact that it might not have
        // its own avatar/name setup
        const mainMarketType = outcome.type?.includes("PLAYER_AWAY")
          ? "FRANCHISE_AWAY"
          : "FRANCHISE_HOME";
        const mainMarketOutcomeCompetitorId = Object.values(
          event.mainMarket.outcomes,
        ).find((market) => market.type === mainMarketType)?.competitorId;

        decoratedOutcomes[outcomeId] = {
          ...outcome,
          competitorId: mainMarketOutcomeCompetitorId ?? outcome.competitorId,
          actualCompetitorId: outcome.competitorId, // added for render styles - so we have the actual id rather than the team
        };

        return decoratedOutcomes;
      }

      decoratedOutcomes[outcomeId] = outcome;

      return decoratedOutcomes;
    },
    {},
  );

const useBettingEventWithMarkets = (
  eventId: NullableMaybe<string>,
  marketTypes: MarketType,
) => {
  const canViewLiveBets = useHasPermission("viewLiveMarkets");
  const { event, isLoading: eventLoading } = useBettingEvent(eventId);
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const { markets, isLoading: marketsLoading } = useEventMarkets(
    eventId,
    marketTypes,
    "betting",
  );

  const { competitors, isLoading: competitorsLoading } =
    useBettingEventCompetitors(eventId);

  const isLoading =
    eventLoading || marketsLoading || competitorsLoading || codesLoading;

  const eventWithMarkets = useMemo(() => {
    if (isLoading || !event || !competitors) return;

    return {
      ...event,
      competitors: Object.fromEntries(
        competitors.map((competitor) => [competitor.id, competitor]),
      ),
      markets: (markets || [])
        .filter(
          (market) =>
            isVisibleOnTab(marketTypes, market) &&
            filterMarkets(market, permissions, codes, event),
        )
        .map((market) => ({
          ...market,
          outcomes: decorateOutcomes(market.outcomes, event),
        })),
    };
  }, [
    isLoading,
    racingEvent,
    markets,
    canViewLiveBets,
    event,
    permissions,
    competitors,
    codes,
    marketTypes,
  ]);

  return {
    event: eventWithMarkets,
    isLoading,
  };
};

const useRacingEventWithMarkets = (
  eventId: NullableMaybe<string>,
  marketTypes: MarketType,
) => {
  const canViewLiveBets = useHasPermission("viewLiveMarkets");
  const { event, isLoading: eventLoading } = useRacingEvent(eventId);
  const { codes, isLoading: codesLoading } = useCampaigns();
  const permissions = usePermissions();

  const { markets, isLoading: marketsLoading } = useEventMarkets(
    eventId,
    marketTypes,
    "racing",
  );

  const { competitors, isLoading: competitorsLoading } =
    useRacingEventCompetitors(eventId);

  const isLoading =
    eventLoading || marketsLoading || competitorsLoading || codesLoading;

  const eventWithMarkets = useMemo(() => {
    if (isLoading || !event || !markets || !competitors) return;

    return {
      ...event,
      hub: "racing",
      competitors: Object.fromEntries(
        competitors.map((competitor) => [competitor.id, competitor]),
      ),
      markets: (markets || []).filter((market) =>
        filterMarkets(market, permissions, codes, event),
      ),
    };
  }, [
    isLoading,
    racingEvent,
    markets,
    canViewLiveBets,
    event,
    permissions,
    competitors,
    codes,
  ]);

  return {
    event: eventWithMarkets,
    isLoading,
  };
};

const useEvent = (eventId: NullableMaybe<string>, marketTypes: MarketType) => {
  const { event: racingEvent, isLoading: racingEventLoading } =
    useRacingEventWithMarkets(eventId, marketTypes);
  const { event: bettingEvent, isLoading: bettingEventLoading } =
    useBettingEventWithMarkets(eventId, marketTypes);

  const event = racingEvent ?? bettingEvent;

  return {
    event,
    isLoading: racingEventLoading || bettingEventLoading,
  };
};

const isBettingEvent = (
  event: ProcessedEvent | ProcessedRacingEvent | ProcessedBettingEvent,
): event is BettingEventWithMarkets => {
  return "mainMarket" in event;
};

const isRacingEvent = (
  event: NullableMaybe<
    ProcessedEvent | ProcessedRacingEvent | ProcessedBettingEvent
  >,
): event is ProcessedRacingEvent => {
  return !!event && event.eventType === "RACE";
};

type ProcessedBettingEvent = Always<
  ReturnType<typeof useBettingEvent>["event"]
>;

type BettingEventWithMarkets = Always<
  ReturnType<typeof useBettingEventWithMarkets>["event"]
>;

type RacingEventWithMarkets = Always<
  ReturnType<typeof useRacingEventWithMarkets>["event"]
>;

type ProcessedMarkets = Always<ReturnType<typeof useEventMarkets>["markets"]>;

type ProcessedMarket = ProcessedMarkets[number];

type DecoratedOutcomes = Record<
  string,
  ProcessedMarket["outcomes"][string] & { actualCompetitorId?: string }
>;

type ProcessedOutcomes = ReturnType<typeof decorateOutcomes>;

type ProcessedOutcome = ProcessedOutcomes["0"];

type ProcessedEvent = Always<ReturnType<typeof useEvent>["event"]>;

type ProcessedBettingCompetitors = Always<
  ReturnType<typeof useBettingEventCompetitors>["competitors"]
>;

type ProcessedBettingCompetitor = ProcessedBettingCompetitors[number];

type ProcessedBettingEventStatus = Always<
  ReturnType<typeof useBettingEvent>["event"]
>["status"];

export {
  useEvent,
  useBettingEvent,
  useBettingEventWithMarkets,
  useRacingEventWithMarkets,
  useBettingMarket,
  isVisibleOnTab,
  useEventMarkets,
  isBettingEvent,
  isRacingEvent,
};

export type {
  BettingEventWithMarkets,
  RacingEventWithMarkets,
  ProcessedMarkets,
  ProcessedMarket,
  ProcessedBettingEvent,
  ProcessedOutcomes,
  ProcessedOutcome,
  ProcessedEvent,
  ProcessedBettingCompetitors,
  ProcessedBettingCompetitor,
  ProcessedBettingEventStatus,
};
